import { useState } from 'react';
import toast from 'react-hot-toast';

import { Cross1Icon } from '@radix-ui/react-icons';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { authService, stripeService } from '@/shared/api';
import { logger } from '@/shared/lib/logger';
import { useModalStore } from '@/shared/stores/modal-store';
import { IOSSwitch } from '@/shared/ui/ios-switch';

export const ThreeDSModal = () => {
  const { user } = useAuthStore();
  const { threeDSData, isProcessing3DS, isModalOpen, closeThreeDSModal, setProcessing3DS } = useModalStore();
  const [isUpdatingAutoRefill, setIsUpdatingAutoRefill] = useState(false);
  const [optimisticAutoRefillEnabled, setOptimisticAutoRefillEnabled] = useState<boolean | null>(null);

  const isOpen = isModalOpen('threeDS');

  // Get current AutoRefill status (with optimistic update)
  const isAutoRefillEnabled = optimisticAutoRefillEnabled !== null 
    ? optimisticAutoRefillEnabled 
    : user?.autoRefillEnabled || false;

  if (!isOpen || !threeDSData) return null;

  const { paymentAmount, creditAmount, clientSecret } = threeDSData;
  const formattedAmount = (paymentAmount / 100).toFixed(2);
  const currencySymbol = '$'; // Default to USD for now

  const handleAuthenticate = async () => {
    if (!user?.stripeDefaultPaymentMethodId) {
      toast.error('Missing payment information');
      return;
    }

    setProcessing3DS(true);

    try {
      // Use existing Stripe service to handle 3DS
      await stripeService.confirmPaymentWith3DSecure(clientSecret, user.stripeDefaultPaymentMethodId);

      // Payment confirmed! Close modal
      closeThreeDSModal();
      toast.success('Payment authentication successful!');
    } catch (error) {
      logger.api.error('3DS authentication failed', { error });
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
      toast.error(`Payment authentication failed: ${errorMessage}`);
    } finally {
      setProcessing3DS(false);
    }
  };

  const handleToggleAutoRefill = async (enabled: boolean) => {
    if (!user) return;

    // Check if user has payment method when trying to enable
    if (enabled && !user.stripeDefaultPaymentMethodId) {
      toast.error('Please add a payment method first to enable AutoRefill');
      return;
    }

    // Optimistic update - immediately change UI
    setOptimisticAutoRefillEnabled(enabled);
    setIsUpdatingAutoRefill(true);

    try {
      const response = await authService.updateProfile({ autoRefillEnabled: enabled });

      // Check if response is successful
      if (response.statusCode === 200 && response.type === 'success') {
        // Update auth store
        const currentUser = useAuthStore.getState().user;
        
        if (currentUser) {
          useAuthStore.getState().updateUser({
            autoRefillEnabled: response.data.autoRefillEnabled,
          });
        }

        toast.success(enabled ? 'AutoRefill enabled' : 'AutoRefill disabled');
        setOptimisticAutoRefillEnabled(null);
      } else {
        throw new Error(response.message || 'Failed to update AutoRefill setting');
      }
    } catch (error) {
      // Revert optimistic update on error
      setOptimisticAutoRefillEnabled(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update AutoRefill setting';
      toast.error(errorMessage);
      logger.api.error('Failed to toggle AutoRefill', { error });
    } finally {
      setIsUpdatingAutoRefill(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing3DS) {
      closeThreeDSModal();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={!isProcessing3DS ? handleClose : undefined}
      />

      {/* Modal */}
      <div className="relative bg-zinc-800 rounded-xl border border-zinc-700 shadow-2xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-700">
          <h2 className="text-xl font-semibold text-white">Payment Authentication Required</h2>
          {!isProcessing3DS && (
            <button
              onClick={handleClose}
              className="text-zinc-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-zinc-700"
            >
              <Cross1Icon className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Payment Info */}
          <div className="bg-zinc-700/30 rounded-lg p-4 border border-zinc-600">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-300 text-sm">Amount to charge:</span>
              <span className="text-white font-medium">
                {currencySymbol}
                {formattedAmount}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-300 text-sm">Credits to receive:</span>
              <span className="text-violet-400 font-medium">{creditAmount} credits</span>
            </div>
          </div>

          {/* Info Text */}
          <div className="text-zinc-300 text-sm leading-relaxed">
            <p className="mb-3">
              {user?.credits && user.credits < 500 
                ? 'Your credit balance is low. AutoRefill will maintain a minimum balance for uninterrupted creation.'
                : 'AutoRefill is topping up your account to maintain a minimum balance for future creations.'}
            </p>
            <p className="mb-2">
              Your bank requires additional authentication for this payment. You will be redirected
              to complete the verification process.
            </p>
            <p className="text-xs text-zinc-400">
              After successful authentication, credits will be automatically added to your account
              and your generation will continue.
            </p>
          </div>

          {/* AutoRefill Toggle */}
          <div className="flex items-center justify-between pt-4 pb-2 border-t border-zinc-700">
            <div className="flex-1">
              <p className="text-sm text-zinc-300 mb-1">AutoRefill Settings</p>
              <p className="text-xs text-zinc-400">
                Turn off AutoRefill to avoid automatic payments in the future
              </p>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <IOSSwitch
                checked={isAutoRefillEnabled}
                onChange={handleToggleAutoRefill}
                disabled={isUpdatingAutoRefill || isProcessing3DS}
                size="sm"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleClose}
              disabled={isProcessing3DS}
              className="flex-1 px-4 py-3 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleAuthenticate}
              disabled={isProcessing3DS}
              className="flex-1 px-4 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {isProcessing3DS ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Authenticating...</span>
                </>
              ) : (
                <span>Authenticate Payment</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};